/**
 * Region de notificaciones.
 *
 * Vive en una region `aria-live="polite"` fija: los lectores de pantalla anuncian
 * el aviso sin robar el foco, que es lo correcto cuando el mensaje es la
 * consecuencia de una accion que el usuario acaba de hacer.
 *
 * En movil los avisos entran desde abajo (donde esta el pulgar) y en escritorio
 * desde la esquina inferior derecha, lejos del contenido que se esta leyendo.
 */

import clsx from 'clsx';

import { IconAlert, IconCheck, IconClose, IconInfo } from '@/components/shell/icons';
import { useToastStore, type ToastTone } from '@/components/shell/toast';

const ESTILO_POR_TONO: Record<ToastTone, string> = {
  info: 'border-line bg-surface text-fg',
  success: 'border-success/30 bg-success-soft text-fg',
  warning: 'border-warning/30 bg-warning-soft text-fg',
  danger: 'border-danger/30 bg-danger-soft text-fg',
};

const COLOR_ICONO: Record<ToastTone, string> = {
  info: 'text-accent',
  success: 'text-success',
  warning: 'text-warning',
  danger: 'text-danger',
};

function IconoPorTono({ tone }: { tone: ToastTone }) {
  const clase = clsx('h-5 w-5 shrink-0', COLOR_ICONO[tone]);
  if (tone === 'success') return <IconCheck className={clase} />;
  if (tone === 'warning' || tone === 'danger') return <IconAlert className={clase} />;
  return <IconInfo className={clase} />;
}

export function ToastHost() {
  const toasts = useToastStore((estado) => estado.toasts);
  const dismiss = useToastStore((estado) => estado.dismiss);

  return (
    <div
      aria-live="polite"
      aria-relevant="additions text"
      className="pointer-events-none fixed inset-x-3 bottom-3 z-50 flex flex-col gap-2 sm:inset-x-auto sm:right-6 sm:bottom-6 sm:w-96"
    >
      {toasts.map((toast) => (
        <div
          key={toast.id}
          role={toast.tone === 'danger' ? 'alert' : 'status'}
          className={clsx(
            'pointer-events-auto flex items-start gap-3 rounded-lg border p-3 shadow-lg animate-slide-in-right',
            ESTILO_POR_TONO[toast.tone],
          )}
        >
          <IconoPorTono tone={toast.tone} />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium">{toast.title}</p>
            {toast.description !== undefined && (
              <p className="mt-0.5 text-sm text-fg-muted">{toast.description}</p>
            )}
          </div>
          <button
            type="button"
            onClick={() => {
              dismiss(toast.id);
            }}
            className="-m-1 rounded-md p-1 text-fg-faint transition-colors hover:bg-fg/5 hover:text-fg"
            aria-label={`Cerrar aviso: ${toast.title}`}
          >
            <IconClose className="h-4 w-4" />
          </button>
        </div>
      ))}
    </div>
  );
}
